import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { submitToCustomerIO } from '@/utils/apiUtils';

const GetStarted = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Track form submission attempt
    if ((window as any).gtag) {
      (window as any).gtag('event', 'form_submit_attempt', {
        form_name: 'Get Started',
        form_location: '/get-started'
      });
    }
    
    try {
      await submitToCustomerIO({
        email,
        name,
        company,
        message
      }, 'get-started');

      // Track successful submission
      if ((window as any).gtag) {
        (window as any).gtag('event', 'form_submit_success', {
          form_name: 'Get Started',
          form_location: '/get-started'
        });
      }

      toast({
        title: "Information submitted!",
        description: "We'll be in touch shortly to get you set up.",
      });
      
      // Reset form
      setEmail('');
      setName('');
      setCompany('');
      setMessage('');
    } catch (error) {
      // Track failed submission
      if ((window as any).gtag) {
        (window as any).gtag('event', 'form_submit_error', {
          form_name: 'Get Started',
          form_location: '/get-started',
          error_message: error instanceof Error ? error.message : 'Unknown error'
        });
      }

      toast({
        title: "Error submitting information",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Get Started with Fordje",
    "description": "Get started with Fordje's regulatory data platform",
    "url": "https://www.fordje.com/get-started",
    "mainEntity": {
      "@type": "Organization",
      "name": "Fordje",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "Sales",
        "availableLanguage": "English"
      }
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Get Started - Fordje Regulatory Data Platform | Building Codes & Permits</title>
        <meta name="description" content="Fordje gives solar, storage, and EV teams every local regulation and utility requirement for a jurisdiction, from feasibility through inspection." />
        <meta name="keywords" content="Fordje get started, regulatory data onboarding, building codes setup, construction software start, permit requirements access" />
        <link rel="canonical" href="https://www.fordje.com/get-started" />
        
        <meta property="og:title" content="Get Started - Fordje Regulatory Data Platform" />
        <meta property="og:description" content="Get started with Fordje and navigate regulations without surprises. Connect with our team today." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fordje.com/get-started" />
        <meta property="og:image" content="https://www.fordje.com/og-image.png" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Get Started - Fordje Regulatory Data Platform" />
        <meta name="twitter:description" content="Get started with Fordje and navigate regulations without surprises." />
        <meta name="twitter:image" content="https://www.fordje.com/og-image.png" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <Header />
      <main className="overflow-hidden">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-background to-accent/5 relative overflow-hidden pt-28 pb-16">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-accent/10 opacity-30"></div>
          
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center relative z-10">
              <h1 className="mb-6 font-extrabold gradient-text animate-fade-in text-4xl md:text-5xl">
                Let's get you set up.
              </h1>
              <p className="text-xl md:text-2xl mb-6 text-foreground/80 max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.1s' }}>
                Answer a few questions to connect with our team right away.
              </p>
            </div>
          </div>
        </section>
        
        {/* Get Started Form */}
        <section className="py-16 bg-gradient-to-b from-background to-secondary/20">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <div className="bg-card border border-border/50 p-8 md:p-12 rounded-xl shadow-2xl shadow-primary/5 animate-fade-in backdrop-blur-sm">
                <h2 className="text-3xl font-semibold mb-6 text-foreground">Tell Us About Your Needs</h2>
                <p className="text-foreground/70 mb-8">
                  Share some details about your organization and we'll connect you with the right solution.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-foreground font-medium">Full Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Your name"
                      className="bg-background border-border focus:border-primary/60 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                      maxLength={100}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-foreground font-medium">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="your.email@company.com"
                      className="bg-background border-border focus:border-primary/60 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                      maxLength={100}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="company" className="text-foreground font-medium">Company Name</Label>
                    <Input
                      id="company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      required
                      placeholder="Your company"
                      className="bg-background border-border focus:border-primary/60 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
                      maxLength={100}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-foreground font-medium">Tell us about your project or needs (Optional)</Label>
                    <Textarea
                      id="message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="What markets are you operating in? What challenges are you facing with regulations?"
                      className="bg-background border-border focus:border-primary/60 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground min-h-[120px]"
                      maxLength={500}
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 h-auto text-lg font-semibold shadow-lg shadow-primary/25 transition-all duration-300"
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Connect With Our Team'}
                  </Button>
                  
                  <p className="text-sm text-foreground/60 text-center mt-4">
                    We respect your privacy and will never share your information.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GetStarted;